import React, { useMemo, useRef, useState, useEffect } from "react";

/**
 * DocLanguageSelect (Dropdown version)
 * - Single dropdown (no accordions).
 * - Search + "Show extended" preserved.
 * - Flags + labels + BCP-47 codes preserved.
 * - Manual "Top" order: Auto → English (Intl) → English (US) → Spanish → Bengali → Hindi → Arabic → Urdu → English (UK) → English (Canada)
 *
 * Props:
 *  - value: string (BCP-47) | "auto"  (default "auto")
 *  - onChange: (code: string) => void
 *  - defaultShowExtended?: boolean
 */
const DocLanguageSelect = ({ value = "auto", onChange, defaultShowExtended = false }) => {
  const [query, setQuery] = useState("");
  const [showExtended, setShowExtended] = useState(!!defaultShowExtended);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  // ===== Data =====
  // Each item: { code, label, flag, region, core }
  // NOTE: No global sort — we preserve manual "Top" ordering.
  const items = useMemo(
    () => [
      // --- Top (manual order; keep these FIRST) ---
      { code: "auto", label: "Auto (English)", flag: "🧭", region: "Top", core: true },
      { code: "en", label: "English (International)", flag: "🌍", region: "Top", core: true },
      { code: "en-US", label: "English (United States)", flag: "🇺🇸", region: "Top", core: true },
      { code: "es", label: "Spanish (International)", flag: "🇪🇸", region: "Top", core: true },
      { code: "bn", label: "Bengali", flag: "🇧🇩", region: "Top", core: true },
      { code: "hi", label: "Hindi", flag: "🇮🇳", region: "Top", core: true },
      { code: "ar", label: "Arabic (Standard)", flag: "🌍", region: "Top", core: true },
      { code: "ur", label: "Urdu", flag: "🇵🇰", region: "Top", core: true },
      { code: "en-GB", label: "English (United Kingdom)", flag: "🇬🇧", region: "Top", core: true },
      { code: "en-CA", label: "English (Canada)", flag: "🇨🇦", region: "Top", core: true },

      // --- Other English variants (non-Top) ---
      { code: "en-AU", label: "English (Australia)", flag: "🇦🇺", region: "Oceania", core: true },
      { code: "en-NZ", label: "English (New Zealand)", flag: "🇳🇿", region: "Oceania", core: false },
      { code: "en-IN", label: "English (India)", flag: "🇮🇳", region: "Asia", core: false },

      // --- MENA ---
      { code: "ar-SA", label: "Arabic (Saudi Arabia)", flag: "🇸🇦", region: "MENA", core: true },
      { code: "ar-AE", label: "Arabic (U.A.E.)", flag: "🇦🇪", region: "MENA", core: false },
      { code: "ar-EG", label: "Arabic (Egypt)", flag: "🇪🇬", region: "MENA", core: true },
      { code: "ar-MA", label: "Arabic (Morocco)", flag: "🇲🇦", region: "MENA", core: false },
      { code: "ar-DZ", label: "Arabic (Algeria)", flag: "🇩🇿", region: "MENA", core: false },
      { code: "ar-TN", label: "Arabic (Tunisia)", flag: "🇹🇳", region: "MENA", core: false },
      { code: "ar-JO", label: "Arabic (Jordan)", flag: "🇯🇴", region: "MENA", core: false },
      { code: "ar-IQ", label: "Arabic (Iraq)", flag: "🇮🇶", region: "MENA", core: false },
      { code: "ar-PS", label: "Arabic (Palestine)", flag: "🇵🇸", region: "MENA", core: false },
      { code: "ar-LB", label: "Arabic (Lebanon)", flag: "🇱🇧", region: "MENA", core: false },
      { code: "fa", label: "Persian (Farsi)", flag: "🇮🇷", region: "MENA", core: true },
      { code: "ku", label: "Kurdish (Kurmanji)", flag: "🟨", region: "MENA", core: false },
      { code: "ckb", label: "Kurdish (Sorani)", flag: "🟩", region: "MENA", core: false },
      { code: "he", label: "Hebrew", flag: "🇮🇱", region: "MENA", core: true },
      { code: "tr", label: "Turkish", flag: "🇹🇷", region: "MENA", core: true },
      { code: "az", label: "Azerbaijani", flag: "🇦🇿", region: "MENA", core: false },
      { code: "ber", label: "Berber (Tamazight)", flag: "⛰️", region: "MENA", core: false },
      { code: "kab", label: "Kabyle (Berber)", flag: "⛰️", region: "MENA", core: false },

      // --- Africa ---
      { code: "sw", label: "Swahili", flag: "🇰🇪", region: "Africa", core: true },
      { code: "am", label: "Amharic", flag: "🇪🇹", region: "Africa", core: false },
      { code: "ha", label: "Hausa", flag: "🇳🇬", region: "Africa", core: false },
      { code: "ig", label: "Igbo", flag: "🇳🇬", region: "Africa", core: false },
      { code: "yo", label: "Yoruba", flag: "🇳🇬", region: "Africa", core: false },
      { code: "zu", label: "Zulu", flag: "🇿🇦", region: "Africa", core: false },
      { code: "xh", label: "Xhosa", flag: "🇿🇦", region: "Africa", core: false },
      { code: "st", label: "Sesotho", flag: "🇿🇦", region: "Africa", core: false },
      { code: "sn", label: "Shona", flag: "🇿🇼", region: "Africa", core: false },
      { code: "rw", label: "Kinyarwanda", flag: "🇷🇼", region: "Africa", core: false },
      { code: "lg", label: "Luganda", flag: "🇺🇬", region: "Africa", core: false },
      { code: "so", label: "Somali", flag: "🇸🇴", region: "Africa", core: false },
      { code: "ti", label: "Tigrinya", flag: "🇪🇷", region: "Africa", core: false },
      { code: "om", label: "Oromo (Afaan Oromo)", flag: "🇪🇹", region: "Africa", core: false },
      { code: "bm", label: "Bambara", flag: "🇲🇱", region: "Africa", core: false },
      { code: "wo", label: "Wolof", flag: "🇸🇳", region: "Africa", core: false },
      { code: "rn", label: "Kirundi", flag: "🇧🇮", region: "Africa", core: false },
      { code: "ar-SS", label: "Arabic (South Sudan)", flag: "🇸🇸", region: "Africa", core: false },
      { code: "ff", label: "Fula", flag: "🌍", region: "Africa", core: false },
      { code: "mg", label: "Malagasy", flag: "🇲🇬", region: "Africa", core: false },
      { code: "ts", label: "Tsonga", flag: "🇿🇦", region: "Africa", core: false },
      { code: "tn", label: "Setswana", flag: "🇧🇼", region: "Africa", core: false },
      { code: "ny", label: "Chichewa (Nyanja)", flag: "🇲🇼", region: "Africa", core: false },
      { code: "kab-DZ", label: "Kabyle (Algeria)", flag: "🇩🇿", region: "Africa", core: false },

      // --- Americas ---
      // (Note: "es" lives in Top; avoid duplicate here)
      { code: "es-MX", label: "Spanish (Mexico)", flag: "🇲🇽", region: "Americas", core: true },
      { code: "es-AR", label: "Spanish (Argentina)", flag: "🇦🇷", region: "Americas", core: false },
      { code: "es-CL", label: "Spanish (Chile)", flag: "🇨🇱", region: "Americas", core: false },
      { code: "es-CO", label: "Spanish (Colombia)", flag: "🇨🇴", region: "Americas", core: false },
      { code: "es-PE", label: "Spanish (Peru)", flag: "🇵🇪", region: "Americas", core: false },
      { code: "es-VE", label: "Spanish (Venezuela)", flag: "🇻🇪", region: "Americas", core: false },
      { code: "pt-BR", label: "Portuguese (Brazil)", flag: "🇧🇷", region: "Americas", core: true },
      { code: "fr-CA", label: "French (Canada)", flag: "🇨🇦", region: "Americas", core: true },
      { code: "ht", label: "Haitian Creole", flag: "🇭🇹", region: "Americas", core: false },
      { code: "qu", label: "Quechua", flag: "🇵🇪", region: "Americas", core: false },
      { code: "gn", label: "Guaraní", flag: "🇵🇾", region: "Americas", core: false },
      { code: "ay", label: "Aymara", flag: "🇧🇴", region: "Americas", core: false },
      { code: "cr", label: "Cree", flag: "🪶", region: "Americas", core: false },
      { code: "nl-SR", label: "Dutch (Suriname)", flag: "🇸🇷", region: "Americas", core: false },

      // --- Asia ---
      { code: "zh-CN", label: "Chinese (Simplified, China)", flag: "🇨🇳", region: "Asia", core: true },
      { code: "zh-TW", label: "Chinese (Traditional, Taiwan)", flag: "🇹🇼", region: "Asia", core: true },
      { code: "zh-HK", label: "Chinese (Traditional, Hong Kong)", flag: "🇭🇰", region: "Asia", core: false },
      { code: "ja", label: "Japanese", flag: "🇯🇵", region: "Asia", core: true },
      { code: "ko", label: "Korean", flag: "🇰🇷", region: "Asia", core: true },
      { code: "vi", label: "Vietnamese", flag: "🇻🇳", region: "Asia", core: true },
      { code: "th", label: "Thai", flag: "🇹🇭", region: "Asia", core: true },
      { code: "id", label: "Indonesian", flag: "🇮🇩", region: "Asia", core: true },
      { code: "ms", label: "Malay", flag: "🇲🇾", region: "Asia", core: false },
      { code: "fil", label: "Filipino (Tagalog)", flag: "🇵🇭", region: "Asia", core: true },
      // (Top already includes hi, bn, ur)
      { code: "pa", label: "Punjabi", flag: "🇵🇰", region: "Asia", core: false },
      { code: "mr", label: "Marathi", flag: "🇮🇳", region: "Asia", core: false },
      { code: "gu", label: "Gujarati", flag: "🇮🇳", region: "Asia", core: false },
      { code: "ta", label: "Tamil", flag: "🇮🇳", region: "Asia", core: true },
      { code: "te", label: "Telugu", flag: "🇮🇳", region: "Asia", core: false },
      { code: "ml", label: "Malayalam", flag: "🇮🇳", region: "Asia", core: false },
      { code: "kn", label: "Kannada", flag: "🇮🇳", region: "Asia", core: false },
      { code: "ne", label: "Nepali", flag: "🇳🇵", region: "Asia", core: false },
      { code: "si", label: "Sinhala", flag: "🇱🇰", region: "Asia", core: false },
      { code: "km", label: "Khmer", flag: "🇰🇭", region: "Asia", core: false },
      { code: "lo", label: "Lao", flag: "🇱🇦", region: "Asia", core: false },
      { code: "my", label: "Burmese (Myanmar)", flag: "🇲🇲", region: "Asia", core: false },
      { code: "mn", label: "Mongolian", flag: "🇲🇳", region: "Asia", core: false },
      { code: "kk", label: "Kazakh", flag: "🇰🇿", region: "Asia", core: false },
      { code: "uz", label: "Uzbek", flag: "🇺🇿", region: "Asia", core: false },
      { code: "tg", label: "Tajik", flag: "🇹🇯", region: "Asia", core: false },
      { code: "ps", label: "Pashto", flag: "🇦🇫", region: "Asia", core: false },
      { code: "fa-AF", label: "Dari (Afghanistan)", flag: "🇦🇫", region: "Asia", core: false },
      { code: "ky", label: "Kyrgyz", flag: "🇰🇬", region: "Asia", core: false },

      // --- Europe ---
      { code: "fr", label: "French (France)", flag: "🇫🇷", region: "Europe", core: true },
      { code: "de", label: "German (Germany)", flag: "🇩🇪", region: "Europe", core: true },
      { code: "es-ES", label: "Spanish (Spain)", flag: "🇪🇸", region: "Europe", core: true },
      { code: "pt-PT", label: "Portuguese (Portugal)", flag: "🇵🇹", region: "Europe", core: true },
      { code: "it", label: "Italian", flag: "🇮🇹", region: "Europe", core: true },
      { code: "nl", label: "Dutch (Netherlands)", flag: "🇳🇱", region: "Europe", core: true },
      { code: "sv", label: "Swedish", flag: "🇸🇪", region: "Europe", core: true },
      { code: "no", label: "Norwegian", flag: "🇳🇴", region: "Europe", core: true },
      { code: "da", label: "Danish", flag: "🇩🇰", region: "Europe", core: true },
      { code: "fi", label: "Finnish", flag: "🇫🇮", region: "Europe", core: true },
      { code: "pl", label: "Polish", flag: "🇵🇱", region: "Europe", core: true },
      { code: "cs", label: "Czech", flag: "🇨🇿", region: "Europe", core: true },
      { code: "sk", label: "Slovak", flag: "🇸🇰", region: "Europe", core: false },
      { code: "hu", label: "Hungarian", flag: "🇭🇺", region: "Europe", core: true },
      { code: "ro", label: "Romanian", flag: "🇷🇴", region: "Europe", core: true },
      { code: "bg", label: "Bulgarian", flag: "🇧🇬", region: "Europe", core: true },
      { code: "el", label: "Greek", flag: "🇬🇷", region: "Europe", core: true },
      { code: "uk", label: "Ukrainian", flag: "🇺🇦", region: "Europe", core: true },
      { code: "ru", label: "Russian", flag: "🇷🇺", region: "Europe", core: true },
      { code: "sr", label: "Serbian", flag: "🇷🇸", region: "Europe", core: false },
      { code: "hr", label: "Croatian", flag: "🇭🇷", region: "Europe", core: false },
      { code: "sl", label: "Slovenian", flag: "🇸🇮", region: "Europe", core: false },
      { code: "lt", label: "Lithuanian", flag: "🇱🇹", region: "Europe", core: false },
      { code: "lv", label: "Latvian", flag: "🇱🇻", region: "Europe", core: false },
      { code: "et", label: "Estonian", flag: "🇪🇪", region: "Europe", core: false },
      { code: "ga", label: "Irish", flag: "🇮🇪", region: "Europe", core: false },
      { code: "is", label: "Icelandic", flag: "🇮🇸", region: "Europe", core: false },
      { code: "mt", label: "Maltese", flag: "🇲🇹", region: "Europe", core: false },
      { code: "sq", label: "Albanian", flag: "🇦🇱", region: "Europe", core: false },
      { code: "bs", label: "Bosnian", flag: "🇧🇦", region: "Europe", core: false },
      { code: "mk", label: "Macedonian", flag: "🇲🇰", region: "Europe", core: false },
      { code: "cy", label: "Welsh", flag: "🏴", region: "Europe", core: false },
      { code: "eu", label: "Basque", flag: "🏴", region: "Europe", core: false },
      { code: "ca", label: "Catalan", flag: "🏴", region: "Europe", core: false },
      { code: "gl", label: "Galician", flag: "🇪🇸", region: "Europe", core: false },

      // --- Oceania (extras besides English) ---
      { code: "mi", label: "Māori", flag: "🇳🇿", region: "Oceania", core: false },
      { code: "sm", label: "Samoan", flag: "🇼🇸", region: "Oceania", core: false },
      { code: "to", label: "Tongan", flag: "🇹🇴", region: "Oceania", core: false },
      { code: "fj", label: "Fijian", flag: "🇫🇯", region: "Oceania", core: false },
      { code: "bi", label: "Bislama", flag: "🇻🇺", region: "Oceania", core: false },
      { code: "haw", label: "Hawaiian", flag: "🏝️", region: "Oceania", core: false },
    ],
    []
  );

  // ---- Filtering (kept identical logic) ----
  const basePool = useMemo(
    () => (showExtended ? items : items.filter((i) => i.core || i.region === "Top")),
    [items, showExtended]
  );

  const topCodes = useMemo(
    () => ["auto", "en", "en-US", "es", "bn", "hi", "ar", "ur", "en-GB", "en-CA"],
    []
  );

  // Build visible list: Top (manual order) + others (alpha) with query applied to both
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();

    const match = (i) =>
      !q ||
      i.label.toLowerCase().includes(q) ||
      i.code.toLowerCase().includes(q) ||
      i.region.toLowerCase().includes(q);

    const topItems = basePool.filter((i) => topCodes.includes(i.code) && match(i));
    // Deduplicate by code and exclude Top
    const otherItems = basePool
      .filter((i) => !topCodes.includes(i.code) && match(i))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Reorder top strictly by our manual order
    const topOrdered = topCodes
      .map((code) => topItems.find((t) => t.code === code))
      .filter(Boolean);

    return [...topOrdered, ...otherItems];
  }, [basePool, query, topCodes]);

  // Current selection details
  const current = useMemo(() => {
    const code = value || "auto";
    return items.find((i) => i.code === code) || items[0];
  }, [items, value]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Keyboard navigation inside list
  const onKeyDown = (e) => {
    if (!isOpen) return;
    if (!visible.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((idx) => (idx + 1) % visible.length);
      scrollActiveIntoView();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((idx) => (idx - 1 + visible.length) % visible.length);
      scrollActiveIntoView();
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = visible[Math.max(0, activeIdx)];
      if (pick) selectCode(pick.code);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  const scrollActiveIntoView = () => {
    requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) return;
      const active = list.querySelector('[data-active="true"]');
      if (!active) return;
      const top = active.offsetTop;
      const bottom = top + active.offsetHeight;
      if (top < list.scrollTop) list.scrollTop = top;
      else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
    });
  };

  const selectCode = (code) => {
    onChange && onChange(code);
    setIsOpen(false);
  };

  // ===== Styles =====
  const s = {
    wrap: { position: "relative", border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#fff" },
    headerRow: { display: "flex", alignItems: "center", gap: 8 },
    trigger: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      background: "#fff",
      cursor: "pointer",
      fontSize: 14,
    },
    code: { marginLeft: "auto", fontFamily: "monospace", fontSize: 12, color: "#374151" },
    flag: { fontSize: 18, width: 22, textAlign: "center" },
    chevron: { marginLeft: 8, fontSize: 12, color: "#6b7280" },

    // Dropdown panel
    panel: {
      position: "absolute",
      zIndex: 50,
      top: "100%",
      left: 12,
      right: 12,
      marginTop: 8,
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      background: "#fff",
      boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
      padding: 12,
    },
    header: { display: "flex", gap: 12, alignItems: "center", marginBottom: 10, flexWrap: "wrap" },
    search: {
      flex: 1,
      minWidth: 220,
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
    },
    toggle: {
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      background: showExtended ? "#111827" : "#fff",
      color: showExtended ? "#fff" : "#111827",
      cursor: "pointer",
      fontWeight: 600,
    },

    list: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: 6,
      paddingBottom: 4,
      maxHeight: 320,
      overflowY: "auto",
      outline: "none",
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px",
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      background: "#fafafa",
      cursor: "pointer",
    },
    selected: { borderColor: "#111827", background: "#f3f4f6" },
    active: { boxShadow: "0 0 0 2px rgba(17,24,39,0.2) inset" },
    empty: { fontSize: 13, color: "#6b7280", padding: "8px 0 12px" },
  };

  return (
    <div ref={wrapRef} style={s.wrap} onKeyDown={onKeyDown}>
      <div style={s.headerRow}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen((v) => !v);
            setActiveIdx(-1);
          }}
          style={s.trigger}
          title={`${current.label} — ${current.code}`}
        >
          <span style={s.flag}>{current.flag}</span>
          <span>{current.label}</span>
          <span style={s.code}>{current.code}</span>
          <span style={s.chevron}>{isOpen ? "▴" : "▾"}</span>
        </button>
      </div>

      {isOpen && (
        <div role="dialog" aria-label="Language selector" style={s.panel}>
          <div style={s.header}>
            <input
              aria-label="Search languages"
              placeholder="Search by language, country or code…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(-1);
              }}
              style={s.search}
            />
            <button type="button" onClick={() => setShowExtended((v) => !v)} style={s.toggle}>
              {showExtended ? "Hide extended" : "Show extended"}
            </button>
          </div>

          <div
            ref={listRef}
            role="listbox"
            tabIndex={0}
            aria-activedescendant={activeIdx >= 0 ? `lang-opt-${activeIdx}` : undefined}
            style={s.list}
          >
            {visible.length === 0 && <div style={s.empty}>No matches.</div>}

            {visible.map((opt, idx) => {
              const selected = (value || "auto") === opt.code;
              const isActive = idx === activeIdx;
              return (
                <div
                  id={`lang-opt-${idx}`}
                  key={`${opt.code}`}
                  role="option"
                  aria-selected={selected}
                  data-active={isActive ? "true" : "false"}
                  style={{
                    ...s.item,
                    ...(selected ? s.selected : {}),
                    ...(isActive ? s.active : {}),
                  }}
                  onMouseEnter={() => setActiveIdx(idx)}
                  onClick={() => selectCode(opt.code)}
                  title={`${opt.label} — ${opt.code}`}
                >
                  <span style={s.flag}>{opt.flag}</span>
                  <span>{opt.label}</span>
                  <span style={s.code}>{opt.code}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocLanguageSelect;
